import { useNavigate } from "@tanstack/react-router";
import { LayoutGrid, Rows3 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsWorkingMode } from "@/contexts/AppModeContext";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import { useAuth } from "@/hooks/useAuth";
import { useCookingSessions } from "@/hooks/cooking-sessions/queries";
import { multiViewSessionsParams, resolveView, sessionTone } from "@/lib/multi-view";
import { SessionChip } from "./SessionChip";
import { clearMultiViewSelection, useSelectedSessionIds } from "./selection-store";

/**
 * The chips and the two ways in, for what is ticked on Prep (YC-23).
 *
 * Renders nothing until a recipe is ticked, and only in Working mode. It sits
 * just above the bulk-actions bar, which still owns "Selected: N / Actions":
 * ticking, select-all and those actions are the existing mechanism, and this
 * only adds what Multi View needs on top of it.
 */
export function MultiViewSelectionBar() {
  const ids = useSelectedSessionIds();
  const working = useIsWorkingMode();
  if (!working || ids.length === 0) return null;
  return <SelectionTray ids={ids} />;
}

function SelectionTray({ ids }: { ids: number[] }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { activeLocation } = useActiveLocation();
  const { data } = useCookingSessions(multiViewSessionsParams(activeLocation?.id));
  // Chips are not clocks: late here is what the server last said.
  const chips = resolveView(data?.data ?? [], ids);
  // Sits on the bulk bar's shoulder when that bar is showing, on the floor when it is not.
  const hasBulkActions =
    hasPermission("cooking-session:update") ||
    hasPermission("cooking-session:cook") ||
    hasPermission("cooking-session:delete");

  const open = (view: "tabs" | "multi") => navigate({ to: "/multi-view", search: { view, layout: "grid" } });

  return (
    <div
      role="region"
      aria-label={t`Selected recipes`}
      data-testid="multi-view-selection-bar"
      className={cn(
        "fixed left-1/2 z-50 flex w-[min(52rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-3 rounded-lg border border-(--color-border) bg-(--color-surface-content) p-3 shadow-[0px_24px_48px_-12px_rgba(16,24,40,0.18)]",
        hasBulkActions ? "bottom-28" : "bottom-5",
      )}
    >
      <ul className="flex max-h-24 flex-wrap gap-2 overflow-y-auto" aria-label={t`Recipes in the view`}>
        {chips.map((session) => (
          <li key={session.id}>
            <SessionChip name={session.recipeName} tone={sessionTone(session, session.elapsedSeconds)} />
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={clearMultiViewSelection}>
          {t`Clear`}
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="brand-outline" onClick={() => open("tabs")} className="gap-2">
            <Rows3 />
            {t`Open as tabs`}
          </Button>
          <Button type="button" onClick={() => open("multi")} className="gap-2">
            <LayoutGrid />
            {t`Open Multi View`}
          </Button>
        </div>
      </div>
    </div>
  );
}
